"use client";
import { useState } from "react";
import { PhoneCall } from "lucide-react";

const buttons = [
    {
        icon: <PhoneCall size={24} stroke="url(#icon-grad)" />,
        text: "Call Now",
        href: "tel:+16472063523",
    },
    {
        icon: <InstagramIcon stroke="url(#icon-grad)" />,
        text: "Instagram",
        href: "https://www.instagram.com/dj_sachin_2023/",
    },
    {
        icon: <WhatsAppIcon fill="url(#icon-grad)" />,
        text: "Chat Now",
        href: "https://wa.me/+16472063523",
    },
];

export default function ScrollActionButtons() {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    return (
        <>
            <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                    <linearGradient id="icon-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop stopColor="#ffffff" offset="0%" />
                        <stop stopColor="#74EAF8" offset="100%" />
                    </linearGradient>
                </defs>
            </svg>

            <div className="fixed right-1 top-[50%] -translate-y-1/2 z-50 flex flex-col items-end" style={{ fontFamily: 'Lato' }}>
                {buttons.map((btn, index) => (
                    <div
                        key={index}
                        className="group cursor-pointer mb-5"
                        onMouseEnter={() =>
                            window.innerWidth >= 640 && setHoveredIndex(index)
                        }
                        onMouseLeave={() => window.innerWidth >= 640 && setHoveredIndex(null)}
                        onClick={() => {
                            if (window.innerWidth < 640) {
                                // Mobile toggle
                                setHoveredIndex((prev) => (prev === index ? null : index));
                            }
                        }}
                    >
                        <div className="flex items-center justify-end">
                            {/* Icon Box Wrapper */}
                            <div
                                className="relative z-10 w-12 h-12 md:w-[50px] md:h-[50px] rounded-2xl bg-linear-to-b from-[#ffffff] to-[#74EAF8] p-[2.5px] transition-all duration-300 mr-[-5px]"
                                style={{
                                    boxShadow: hoveredIndex === index
                                        ? "0 0 15px rgba(116, 234, 248, 0.8)"
                                        : "0 0 10px rgba(116, 234, 248, 0.4)",
                                }}
                            >
                                <div className="w-full h-full bg-[#0a0410] rounded-[15px] flex justify-center items-center shadow-[inset_0_0_10px_rgba(116,234,248,0.2)]">
                                    <span
                                        className="transition-transform flex items-center justify-center"
                                        style={{
                                            animation: hoveredIndex === index ? "rotateOnce 0.6s linear" : "none",
                                        }}
                                    >
                                        {btn.icon}
                                    </span>
                                </div>
                            </div>

                            {/* Text Box Wrapper */}
                            <div
                                className={`flex items-center overflow-hidden transition-all duration-300
                                ${hoveredIndex === index ? "w-28 md:w-30 opacity-100" : "w-0 opacity-0"}
                                h-9`}
                            >
                                <div
                                    className="w-full h-full bg-linear-to-b from-[#ffffff] to-[#74EAF8] rounded-r-lg pr-[1.5px] py-[1.5px]"
                                    style={{
                                        boxShadow: hoveredIndex === index ? "5px 0 15px rgba(116, 234, 248, 0.4)" : "none",
                                    }}
                                >
                                    <div className="w-full h-full bg-[#0a0410]/95 backdrop-blur-md rounded-r-[10px] flex items-center pl-6 pr-4 shadow-[inset_0_0_8px_rgba(116,234,248,0.2)]">
                                        <a
                                            href={btn.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full h-full flex items-center justify-center font-semibold text-sm md:text-[17px] whitespace-nowrap bg-clip-text text-transparent bg-linear-to-b from-[#ffffff] to-[#74EAF8]"
                                        >
                                            {btn.text}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

function WhatsAppIcon({ fill = "currentColor" }: { fill?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill={fill}
            viewBox="0 0 24 24"
        >
            <path d="M20.52 3.48A11.87 11.87 0 0 0 12.07 0h-.14A11.89 11.89 0 0 0 0 11.94a11.8 11.8 0 0 0 1.64 6l-1.1 4a1 1 0 0 0 1.23 1.23l4-1.1a11.8 11.8 0 0 0 6 1.64h.14A11.89 11.89 0 0 0 24 11.93a11.87 11.87 0 0 0-3.48-8.45zM12 22a9.83 9.83 0 0 1-5.1-1.4l-.37-.22-3 .83.84-3-.23-.38a9.85 9.85 0 0 1-1.5-5.22 9.94 9.94 0 0 1 9.9-9.9h.12A9.91 9.91 0 0 1 22 11.93 9.94 9.94 0 0 1 12 22zm5.41-7.59c-.3-.15-1.76-.86-2.04-.95s-.48-.15-.68.15-.78.94-.95 1.13-.35.22-.64.07a8.14 8.14 0 0 1-2.4-1.48 8.83 8.83 0 0 1-1.63-2c-.17-.3 0-.46.13-.61s.3-.35.45-.52a2 2 0 0 0 .3-.5.55.55 0 0 0 0-.52c-.07-.15-.68-1.64-.94-2.25s-.5-.52-.68-.53h-.58a1.1 1.1 0 0 0-.8.37 3.35 3.35 0 0 0-1 2.47A5.87 5.87 0 0 0 9 15.53a9.64 9.64 0 0 0 5.76 2.07 5.73 5.73 0 0 0 2.36-.47 3.34 3.34 0 0 0 1.47-2.34c.1-.6-.05-.89-.35-1.03z" />
        </svg>
    );
}

function InstagramIcon({ stroke = "currentColor", size = 24 }: { stroke?: string; size?: number }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
            <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
        </svg>
    );
}
